import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import {
  FileBarChart,
  Search,
  Download,
  SquareArrowOutUpRight,
  Loader2,
  ChevronDown,
  CheckCircle,
  XCircle,
  Shield,
  FileArchive,
  Eye,
  Trash2,
  X,
} from "lucide-react";
import { useToastStore } from "../../stores/toastStore";
import { PageShell } from "../components/layout/PageShell";
import { DataTable, cycleSortDir, type Column } from "../components/DataTable";
import { TableToolbar } from "../components/TableToolbar";
import {
  EntityCardGrid,
  MetaDates,
  StatTile,
  StatTiles,
  TitleTooltip,
} from "../components/EntityCardGrid";
import { Modal, ModalShell } from "../components/Modal";
import {
  getAPI,
  downloadFile,
  postPagedSearchAbsolute,
} from "../../api/frameworksApi";
import { ReportWaterLoader } from "./Report2/audit-report/ReportWaterLoader";
import {
  safeFormatDate,
  getPageSizeOptions,
  resolvePageSizeForViewMode,
} from "../../utils/formatters";
import { REPORTS_URL } from "../../constants";

/* ── Types ──────────────────────────────────────────────── */

interface AssessmentPlan {
  planId: string;
  title: string;
  assessmentStatus: string;
  controlCount: number;
  passed: number;
  failed: number;
  compliancePercent: number;
}

interface ReportActions {
  summary: string;
  preview: string;
  export: string;
  exportWithEvidence: string;
}

interface ReportRow {
  sspId: string;
  systemName: string;
  lifecycleStatus: string;
  createdAt: string;
  updatedAt: string;
  planCount: number;
  hasAssessment: boolean;
  compliancePercent: number;
  overallStatus: string;
  assessmentPlans: AssessmentPlan[];
  actions: ReportActions;
}

/**
 * Sortable column key -> backend field name sent as `sortByColumn` in
 * POST {reports dashboard}/{page}/{size}. Sorting is fully server-side; a key
 * with no entry here is forwarded as-is.
 */
const REPORT_SORT_COLUMNS: Record<string, string> = {
  systemName: "systemName",
  lifecycleStatus: "lifecycleStatus",
  overallStatus: "overallStatus",
  compliancePercent: "compliancePercent",
  planCount: "planCount",
  updatedAt: "updatedAt",
};

/* ── Filename helper ────────────────────────────────────── */

const buildReportFilename = (
  systemName: string,
  withEvidence: boolean,
): string => {
  // Keep the zip name short. Windows embeds the whole file name into the temp
  // path when a file is opened directly from inside the zip, so a long name
  // eats into the 260-char MAX_PATH limit and triggers "path too long".
  const safe =
    (systemName || "")
      .replace(/[\\/:*?"<>|]+/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 40)
      .trim() || "report";
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  // Compact stamp (YYYYMMDD-HHMMSS) — no spaces, fewer characters.
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const suffix = withEvidence ? "-evidence" : "";
  return `${safe}-${stamp}${suffix}.zip`;
};

/* ── Content-Disposition filename parser ────────────────── */

// The backend already names the download with the (short) SSP name via the
// Content-Disposition header. Prefer that over a client-built name so the
// zip stays short and avoids Windows' MAX_PATH "path too long" error.
const filenameFromDisposition = (
  disposition: unknown,
): string | null => {
  if (typeof disposition !== "string") return null;
  // RFC 5987: filename*=UTF-8''<percent-encoded> takes precedence.
  const star = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, "")) || null;
    } catch {
      /* fall through to plain filename */
    }
  }
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain?.[1]?.trim() || null;
};

/* ── Status helpers ─────────────────────────────────────── */

const lifecycleBadge = (status: string) => {
  const s = status?.toLowerCase() || "draft";
  const styles: Record<string, string> = {
    draft:
      "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-500/20 dark:text-slate-400 dark:ring-slate-500/30",
    published:
      "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/20 dark:text-blue-400 dark:ring-blue-500/30",
    in_review:
      "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-500/30",
    approved:
      "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-300 dark:ring-emerald-500/30",
  };
  return (
    <span
      className={`inline-flex items-center justify-center w-[100px] px-2.5 py-0.5 rounded-full text-[10px] font-bold ring-1 tracking-wide ${styles[s] || styles.draft}`}
    >
      {s.replace(/_/g, " ")}
    </span>
  );
};

const OVERALL_STATUS_LABELS: Record<string, string> = {
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  NO_ASSESSMENT: "No Assessment",
  NOT_STARTED: "Not Started",
};

const overallStatusLabel = (status: string): string => {
  const s = status?.toUpperCase() || "NO_ASSESSMENT";
  return OVERALL_STATUS_LABELS[s] || s.replace(/_/g, " ");
};

/** Maps a report's overall status to the shared card/badge accent key
 * (see statusAccent() in EntityCardGrid.tsx). */
const overallAccentKey = (status: string): string => {
  const s = status?.toUpperCase() || "NO_ASSESSMENT";
  switch (s) {
    case "COMPLETED":
      return "published";
    case "IN_PROGRESS":
      return "in_progress";
    case "NO_ASSESSMENT":
      return "rejected";
    case "NOT_STARTED":
    default:
      return "draft";
  }
};

const overallBadge = (status: string) => {
  const s = status?.toUpperCase() || "NO_ASSESSMENT";
  const styles: Record<string, string> = {
    IN_PROGRESS:
      "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-500/30",
    COMPLETED:
      "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-300 dark:ring-emerald-500/30",
    NO_ASSESSMENT:
      "bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/20 dark:text-red-300 dark:ring-red-500/30",
    NOT_STARTED:
      "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-500/20 dark:text-slate-400 dark:ring-slate-500/30",
  };
  return (
    <span
      className={`inline-flex items-center justify-center w-[120px] px-2.5 py-0.5 rounded-full text-[10px] font-bold ring-1 whitespace-nowrap ${styles[s] || styles.NO_ASSESSMENT}`}
    >
      {overallStatusLabel(status)}
    </span>
  );
};

/* ── Compliance bar ─────────────────────────────────────── */

function ComplianceBar({ percent }: { percent: number }) {
  const color =
    percent >= 80
      ? "bg-emerald-500"
      : percent >= 50
        ? "bg-amber-500"
        : "bg-red-500";
  return (
    <div className="inline-flex items-center gap-1.5 min-w-[110px] align-middle">
      <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-white/[0.06] overflow-hidden">
        <div
          className={`h-full rounded-full ${color} transition-all`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-xs font-bold text-foreground w-9 text-right tabular-nums whitespace-nowrap">
        {percent}%
      </span>
    </div>
  );
}

/* ── Expanded row ───────────────────────────────────────── */

function PlanDetails({ plans }: { plans: AssessmentPlan[] }) {
  if (!plans.length)
    return <p className="text-xs d">No assessment plans linked.</p>;
  return (
    <div>
      <table className="w-full text-xs">
        <thead>
          <tr className="d border-b border-border tracking-wide text-[11px]">
            <th className="text-center py-2 pr-3 font-semibold whitespace-nowrap">Plan Title</th>
            <th className="text-center py-2 px-3 font-semibold whitespace-nowrap">Status</th>
            <th className="text-center py-2 px-3 font-semibold whitespace-nowrap">Controls</th>
            <th className="text-center py-2 px-3 font-semibold whitespace-nowrap">Passed</th>
            <th className="text-center py-2 px-3 font-semibold whitespace-nowrap">Failed</th>
            <th className="text-center py-2 pl-3 font-semibold whitespace-nowrap">Compliance</th>
          </tr>
        </thead>
        <tbody>
          {plans.map((p) => (
            <tr
              key={p.planId}
              className="border-b border-border/50 last:border-0"
            >
              <td className="py-2.5 pr-3 text-foreground font-medium break-words">{p.title}</td>
              <td className="py-2.5 px-3 text-center">
                {overallBadge(p.assessmentStatus)}
              </td>
              <td className="py-2.5 px-3 text-center d tabular-nums">
                {p.controlCount}
              </td>
              <td className="py-2.5 px-3 text-center">
                <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 tabular-nums">
                  <CheckCircle className="size-3" />
                  {p.passed}
                </span>
              </td>
              <td className="py-2.5 px-3 text-center">
                <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 tabular-nums">
                  <XCircle className="size-3" />
                  {p.failed}
                </span>
              </td>
              <td className="py-2.5 pl-3 text-center">
                <ComplianceBar percent={p.compliancePercent} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Action icons (with Eye‑icon preview + body overflow hidden) ─ */

function ActionIcons({
  actions,
  sspId,
  systemName,
  onNavigate,
}: {
  actions: ReportActions;
  sspId: string;
  systemName: string;
  onNavigate: (path: string) => void;
}) {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRevealed, setPreviewRevealed] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  // Block body scroll when preview is open
  useEffect(() => {
    if (!previewOpen) return;

    const body = document.body;
    const initialOverflow = body.style.overflow;
    body.style.overflow = "hidden";

    return () => {
      body.style.overflow = initialOverflow;
    };
  }, [previewOpen]);

  const handlePreview = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewOpen(true);
    if (previewHtml) return;

    try {
      const res = await downloadFile(
        toFullUrl(actions.preview),
      );
      if (!res) {
        // Falsy response → BE returned no body. Close the overlay so the
        // user lands back on the reports list instead of an infinite
        // "Loading preview" spinner.
        setPreviewOpen(false);
        useToastStore
          .getState()
          .showError("Error", "Preview unavailable. Please try again.");
        return;
      }
      const text = await new Response(res.data).text();

      setPreviewHtml(text);
      useToastStore.getState().showSuccess("Success", "Preview loaded");
    } catch (err) {
      console.error(err);
      // BE 500 / network error → close the overlay so the page returns to
      // the reports list. Toast surfaces the failure.
      setPreviewOpen(false);
      setPreviewHtml(null);
      useToastStore
        .getState()
        .showError("Error", "Failed to load preview. Please try again.");
    }
  };

  const handleDownload = async (
    e: React.MouseEvent,
    url: string,
    withEvidence: boolean,
  ) => {
    e.stopPropagation();
    const key = withEvidence ? "evidence" : "report";
    setDownloadOpen(false);
    setDownloading(key);
    try {
      const res = await downloadFile(toFullUrl(url));
      if (!res) return;
      const blob = new Blob([res.data]);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      // Use the backend's (short, SSP-named) filename; only build one if the
      // Content-Disposition header is missing or not exposed via CORS.
      a.download =
        filenameFromDisposition(res.headers?.["content-disposition"]) ||
        buildReportFilename(systemName, withEvidence);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      useToastStore.getState().showSuccess("Success", "Download started");
    } catch {
      useToastStore.getState().showError("Error", "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleting(true);
    try {
      useToastStore.getState().showSuccess("Success", "Deleting report");
      // TODO: Implement actual delete API call
      setTimeout(() => {
        useToastStore
          .getState()
          .showSuccess("Success", "Report deleted successfully");
        setDeleting(false);
      }, 2000);
    } catch (err) {
      useToastStore.getState().showError("Error", "Failed to delete report");
      setDeleting(false);
    }
  };

  return (
    <div className="flex items-center justify-center gap-1">
      {/* Preview button (Eye icon) */}
      <button
        onClick={handlePreview}
        className="p-1.5 rounded-lg d hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-500/10 transition-colors"
        title="Preview HTML Report"
      >
        <Eye className="size-4" />
      </button>

      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDownloadOpen((open) => !open);
          }}
          disabled={!!downloading}
          className="p-1.5 rounded-lg d hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
          title="Export Report"
        >
          {downloading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <SquareArrowOutUpRight className="size-4" />
          )}
        </button>
        {/* Portaled to <body> so the card's hover transform isn't the
            modal's containing block (which would render it inside the card
            instead of centered on the viewport). */}
        {createPortal(
          <Modal
            open={downloadOpen}
            onClose={() => setDownloadOpen(false)}
            title="Export Report"
            description="Choose whether to export the report with or without evidence."
            size="sm"
          >
            <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={(e) => handleDownload(e, actions.export, false)}
              disabled={!!downloading}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-500/5 hover:border-indigo-400 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors group disabled:opacity-40"
            >
              {downloading === "report" ? (
                <Loader2 className="size-6 animate-spin text-indigo-600 dark:text-indigo-400" />
              ) : (
                <Download className="size-6 text-indigo-600 dark:text-indigo-400 transition-colors" />
              )}
              <span className="text-sm font-semibold text-center text-indigo-600 dark:text-indigo-400 transition-colors">
                Without Evidence
              </span>
            </button>
            <button
              type="button"
              onClick={(e) =>
                handleDownload(e, actions.exportWithEvidence, true)
              }
              disabled={!!downloading}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-500/5 hover:border-amber-400 dark:hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors group disabled:opacity-40"
            >
              {downloading === "evidence" ? (
                <Loader2 className="size-6 animate-spin text-amber-600 dark:text-amber-400" />
              ) : (
                <FileArchive className="size-6 text-amber-600 dark:text-amber-400 transition-colors" />
              )}
              <span className="text-sm font-semibold text-center text-amber-600 dark:text-amber-400 transition-colors">
                With Evidence
              </span>
            </button>
            </div>
          </Modal>,
          document.body,
        )}
      </div>

      {/* Delete button */}
      {/* <button
        onClick={handleDelete}
        disabled={deleting}
        className="p-1.5 rounded-lg d hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Delete Report"
      >
        {deleting ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Trash2 className="size-4" />
        )}
      </button> */}

      {/* Downloading overlay — portaled to <body> so the card's hover
          transform doesn't become its containing block (which would make
          the fixed overlay jitter/flicker as the mouse moves over the card). */}
      {downloading &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4 bg-card rounded-2xl px-10 py-8 shadow-2xl">
              <div className="relative flex items-center justify-center">
                <div className="absolute size-16 rounded-full bg-indigo-500/20 dark:bg-indigo-500/20 animate-ping" />
                <div className="absolute size-12 rounded-full bg-indigo-500/30 dark:bg-indigo-500/30 animate-pulse" />
                <Download className="relative size-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <p className="text-sm font-semibold text-foreground mt-2">
                Downloading
              </p>
              <p className="text-xs d">
                Please wait while we prepare your report
              </p>
            </div>
          </div>,
          document.body,
        )}

      {/* Full‑screen HTML preview — portaled to <body> for the same reason. */}
      {previewOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex flex-col bg-background">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
              <span className="text-sm font-medium">
                Preview – {systemName} Report
              </span>
              <button
                onClick={() => setPreviewOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-accent text-foreground"
                aria-label="Close preview"
              >
                ✕
              </button>
            </div>

            {/* Scrollable body with hidden scrollbar */}
            {previewHtml && previewRevealed ? (
              <iframe
                srcDoc={previewHtml}
                style={{
                  flex: 1,
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="Report Preview"
                sandbox="allow-same-origin allow-scripts"
              />
            ) : (
              <ReportWaterLoader
                label="Loading preview…"
                complete={!!previewHtml}
                onDone={() => setPreviewRevealed(true)}
              />
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

/* ── URL helper ─────────────────────────────────────────── */

const toFullUrl = (path: string) => {
  if (path.startsWith("http")) return path;
  const normalized =
    path.startsWith("/api/") && !path.startsWith("/api/v1/")
      ? path.replace("/api/", "/api/v1/")
      : path;
  return `${window.BE_URL || ""}${normalized}`;
};

/* ── Main component ─────────────────────────────────────── */

export function ReportsList() {
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  /* ── List state ──
   * Search, sorting and pagination are all resolved by the backend via
   * POST {reports dashboard}/{page}/{size}; `reports` always holds exactly the
   * rows for the current page and is rendered as-is. */
  const [search, setSearch] = useState("");
  // The keyword actually sent to the backend (committed on Enter / icon click).
  const [keyword, setKeyword] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  // Must be a valid option for the default view mode above ("card") — the
  // old flat PAGE_SIZE constant (10) isn't one of [12,24,48,96].
  const [pageSize, setPageSize] = useState(getPageSizeOptions("card")[0]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // 3-state cycle: asc → desc → unsorted (back to the backend's default order),
  // which sends sortByColumn: "" / sortingOrder: "" the way it did on first load.
  const handleSort = (key: string) => {
    const next = cycleSortDir(sortKey, sortDir, key);
    setPage(0); // any order change restarts at the first page
    setSortKey(next.sortKey);
    setSortDir(next.sortDir);
  };

  // The skeleton is only shown before the very first page ever lands; every
  // later fetch keeps the current rows visible (no blank page on paging).
  const firstLoadDoneRef = useRef(false);
  const fetchReports = useCallback(async () => {
    if (!firstLoadDoneRef.current) setLoading(true);
    setError(null);
    try {
      const res = await postPagedSearchAbsolute<ReportRow>(
        REPORTS_URL.dashboard,
        page,
        pageSize,
        {
          keyword: keyword.trim(),
          // Unsorted (the initial state) sends both sort fields as empty.
          sortByColumn: sortKey
            ? (REPORT_SORT_COLUMNS[sortKey] ?? sortKey)
            : "",
          sortingOrder: sortKey ? sortDir : "",
        },
      );
      setReports(res.items);
      setTotal(res.totalItems);
      // The current page can fall off the end of the result set (a larger page
      // size, a narrower search) — step back to the last real page.
      if (res.items.length === 0 && page > 0 && res.totalItems > 0) {
        setPage(Math.max(0, Math.ceil(res.totalItems / pageSize) - 1));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load reports");
      setReports([]);
      setTotal(0);
    } finally {
      firstLoadDoneRef.current = true;
      setLoading(false);
    }
  }, [page, pageSize, keyword, sortKey, sortDir]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  /* ── Search submit ──
   * Typing alone never hits the API: the keyword is only pushed to the
   * backend on Enter / the search button, or the moment the box is cleared. */
  const submitSearch = (value: string) => {
    const next = value.trim();
    if (next === keyword) return;
    setKeyword(next);
    setPage(0);
  };

  /* ── Rows for the current page ──
   * No client-side filtering, sorting or slicing: the backend already applied
   * the keyword, sort column/order and page window. */
  const paged = reports;

  /* ── Columns ── */

  const columns: Column<ReportRow>[] = [
    {
      key: "sno",
      header: "#",
      align: "center",
      render: (_r, idx) => (
        <span className="d font-medium">{page * pageSize + idx + 1}</span>
      ),
    },
    {
      key: "systemName",
      header: "System Name",
      sortable: true,
      align: "left",
      ellipsis: 2,
      tooltip: (r) => r.systemName || "",
      render: (r) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpandedId((prev) => (prev === r.sspId ? null : r.sspId));
            }}
            className="p-0.5 rounded hover:bg-accent transition-colors"
          >
            <ChevronDown
              className={`size-3.5 d transition-transform ${expandedId === r.sspId ? "rotate-180" : ""}`}
            />
          </button>
          <span className="font-medium text-foreground">{r.systemName}</span>
        </div>
      ),
    },
    {
      key: "overallStatus",
      header: "Assessment",
      sortable: true,
      align: "center",
      render: (r) => overallBadge(r.overallStatus),
    },
    {
      key: "compliancePercent",
      header: "Compliance",
      sortable: true,
      align: "center",
      render: (r) => <ComplianceBar percent={r.compliancePercent} />,
    },
    {
      key: "planCount",
      header: "Plans",
      sortable: true,
      align: "right",
      render: (r) => (
        <span className="font-bold text-foreground tabular-nums">
          {r.planCount}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "Created On",
      sortable: true,
      align: "center",
      render: (r) => (
        <span className="d text-xs">
          {new Date(r.createdAt).toLocaleDateString("en-GB")}
        </span>
      ),
    },
    {
      key: "updatedAt",
      header: "Updated On",
      sortable: true,
      align: "center",
      render: (r) => (
        <span className="d text-xs">
          {new Date(r.updatedAt).toLocaleDateString("en-GB")}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      render: (r) => (
        <ActionIcons
          actions={r.actions}
          sspId={r.sspId}
          systemName={r.systemName}
          onNavigate={navigate}
        />
      ),
    },
  ];

  return (
    <PageShell className="flex flex-col flex-1 min-h-0 gap-6">
      {/* Header */}
      <div className="flex-shrink-0 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Report</h1>
          <p className="text-sm d mt-1">
            View compliance reports, assessment summaries, and export audit
            packages
          </p>
        </div>
      </div>

      <EntityCardGrid<ReportRow>
        items={paged}
        loading={loading}
        getKey={(r) => r.sspId}
        getStatus={(r) => overallAccentKey(r.overallStatus)}
        getStatusLabel={(r) => overallStatusLabel(r.overallStatus)}
        onOpen={(r) =>
          setExpandedId((prev) => (prev === r.sspId ? null : r.sspId))
        }
        page={page}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={setPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(0);
        }}
        pageSizeOptions={[...getPageSizeOptions(viewMode)]}
        itemLabel="reports"
        emptyText="No reports available"
        emptyIcon={<FileBarChart className="size-5 d" />}
        viewMode={viewMode}
        onViewModeChange={(mode) => {
          setViewMode(mode);
          // The page-size options differ per view — snap the current value
          // to a valid option for the view being switched to instead of
          // leaving the dropdown holding a value that isn't in the new list.
          setPageSize(resolvePageSizeForViewMode(pageSize, mode));
          setPage(0);
        }}
        toolbarLeft={
          <TableToolbar
            search={{
              value: search,
              onChange: setSearch,
              onSubmit: submitSearch,
              placeholder: "Search by system name",
              ariaLabel: "Search reports",
            }}
            trailing={
              <span className="whitespace-nowrap text-sm text-[#64748b] dark:text-white/60">
                {total} report{total !== 1 ? "s" : ""}
              </span>
            }
          />
        }
        renderTitle={(r) => (
          <TitleTooltip text={r.systemName}>
            <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
              {r.systemName}
            </h3>
          </TitleTooltip>
        )}
        renderHeaderRight={() => (
          <FileBarChart className="size-4 text-indigo-500 dark:text-indigo-400" />
        )}
        renderStats={(r) => (
          <StatTiles>
            <StatTile
              icon={<Shield className="size-3.5" />}
              accent="indigo"
              value={`${Math.round(r.compliancePercent ?? 0)}%`}
              label="Compliance"
            />
            <StatTile
              icon={<FileArchive className="size-3.5" />}
              accent="emerald"
              value={r.planCount}
              label="Plans"
            />
          </StatTiles>
        )}
        renderDates={(r) => (
          <MetaDates
            created={safeFormatDate(r.createdAt)}
            updated={safeFormatDate(r.updatedAt)}
          />
        )}
        renderFooterActions={(r) => (
          <div
            className="flex items-center gap-1 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <ActionIcons
              actions={r.actions}
              sspId={r.sspId}
              systemName={r.systemName}
              onNavigate={navigate}
            />
          </div>
        )}
        renderTable={(rows) => (
          <DataTable
            columns={columns}
            data={rows}
            loading={loading}
            error={error}
            emptyText="No reports available"
            emptyIcon={<FileBarChart className="size-5 d" />}
            itemLabel="reports"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
            rowKey={(r) => r.sspId}
            onRowClick={(r) =>
              setExpandedId((prev) => (prev === r.sspId ? null : r.sspId))
            }
            rowClassName={(r) => (expandedId === r.sspId ? "!border-b-0" : "")}
          />
        )}
      />

      {/* Assessment plans popup — opens when a card/row is clicked */}
      {(() => {
        const report = expandedId
          ? reports.find((r) => r.sspId === expandedId)
          : null;
        return (
          <ModalShell
            open={!!report}
            onClose={() => setExpandedId(null)}
            variant="wide"
          >
            <div className="flex items-center justify-between gap-4 p-5 border-b border-border flex-shrink-0">
              <h2 className="text-lg font-semibold text-foreground leading-tight min-w-0 truncate">
                {report ? `Assessment Plans for ${report.systemName}` : "Assessment Plans"}
              </h2>
              <button
                onClick={() => setExpandedId(null)}
                className="flex-shrink-0 p-1.5 -mr-1.5 rounded-md d hover:text-foreground hover:bg-accent transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {report && <PlanDetails plans={report.assessmentPlans} />}
            </div>
          </ModalShell>
        );
      })()}
    </PageShell>
  );
}
